import { useEffect, useRef } from 'react';
import type { ReactNode, RefObject } from 'react';

/**
 * 覆盖层：抽屉（Drawer）与对话框（Modal）。
 *
 * 为什么要有这两个组件，而不是在页面里随手加一个 `position: fixed` 的 div：
 *
 * 1. **焦点必须能被管理**。抽屉打开后，如果 Tab 键还能走到背后的表格里，
 *    键盘用户会在看不见的地方改数据——这是最典型的"能操作但会出事"的缺陷。
 *    这里做两件事：打开时把焦点收进面板，关闭时还给触发元素。
 * 2. **Esc 要能关**。这是用户对"临时面板"的第一反应。
 * 3. **背景要锁滚动**。抽屉里有一个长表单时，背景跟着滚会把"我在哪"弄丢。
 * 4. **背景要变暗**。不然视觉层级上分不清面板是浮在上面还是嵌在页面里。
 *
 * 两个组件共用同一套打开/关闭逻辑，只差尺寸与对齐方式。
 */

/** 打开时锁住背景滚动；关闭时恢复。多个面板叠开时不互相解锁。 */
let scrollLocks = 0;
function lockScroll(): void {
  scrollLocks += 1;
  if (scrollLocks === 1) {
    document.body.style.overflow = 'hidden';
  }
}

function unlockScroll(): void {
  scrollLocks = Math.max(0, scrollLocks - 1);
  if (scrollLocks === 0) {
    document.body.style.overflow = '';
  }
}

interface OverlayBase {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /** 右上角的次要说明，例如"草稿不会出现在前台" */
  meta?: ReactNode;
  children: ReactNode;
  /** 底部固定操作条（保存/取消） */
  footer?: ReactNode;
  /**
   * 关掉右上角的 ×，并让点遮罩不关闭（Esc 仍走 onClose）。
   *
   * 给"等它自己结束"的面板用：上传进度这类东西关掉并不能中断，
   * × 摆在那里只会让人以为可以取消。
   */
  closable?: boolean;
  /**
   * 要不要那层变暗、可点击关闭的遮罩。默认要。
   *
   * 为「全屏编辑」而加：那时抽屉旁边是一整张可点的页面，遮罩一盖上去，
   * 客户就没法点另一段来切换编辑对象了 —— 而这恰恰是全屏编辑的全部交互。
   * 关掉遮罩后抽屉仍然吃自己的点击，页面照常可点。
   */
  scrim?: boolean;
}

function useOverlayBehaviour(
  open: boolean,
  onClose: () => void,
): {
  panelRef: RefObject<HTMLDivElement | null>;
} {
  const panelRef = useRef<HTMLDivElement | null>(null);
  // 记住打开前的焦点，关闭时还回去
  const restoreRef = useRef<HTMLElement | null>(null);
  /**
   * onClose 存进 ref 而不是进依赖数组。
   *
   * 调用方几乎都会传一个内联箭头函数（`() => { setEditor(null) }`），
   * 每次父组件渲染都是新引用。如果直接进依赖数组，effect 会在每次渲染时
   * 重新执行 —— 而 effect 里会把焦点送进面板的第一���输入框。
   * 后果就是：客户正在打字，父组件因为别的原因重渲染了一次，
   * 焦点被抢回第一个输入框，刚打的字打到了别的框里。
   */
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    restoreRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    lockScroll();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closeRef.current();
        return;
      }
      // Tab 循环锁在面板内
      if (event.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !panel.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    // 打开后把焦点送进面板（第一个可聚焦元素，否则面板本身）
    const timer = window.setTimeout(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const target =
        panel.querySelector<HTMLElement>(
          'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])',
        ) ?? panel;
      target.focus();
    }, 0);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      window.clearTimeout(timer);
      unlockScroll();
      restoreRef.current?.focus?.();
    };
  }, [open]);

  return { panelRef };
}

export function Drawer({
  open,
  onClose,
  title,
  description,
  meta,
  children,
  footer,
  closable = true,
  scrim = true,
  size = 'md',
}: /**
 * 抽屉宽度。默认 720px 是"整页表单"用的；全屏编辑里抽屉要跟页面**同时**可见，
 * 720 会把整屏预览压成一条，所以那一处用 `sm`。
 */
OverlayBase & { size?: 'sm' | 'md' }) {
  const { panelRef } = useOverlayBehaviour(open, onClose);
  if (!open) return null;

  return (
    <div className={`overlay overlay--drawer${scrim ? '' : ' overlay--bare'}`} role="presentation">
      {scrim ? <div className="overlay__scrim" onClick={onClose} aria-hidden="true" /> : null}
      <div
        className={`overlay__panel overlay__panel--drawer${size === 'sm' ? ' overlay__panel--drawer-sm' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={panelRef}
        tabIndex={-1}
      >
        <header className="overlay__head">
          <div className="overlay__headtext">
            <h2 className="overlay__title">{title}</h2>
            {description ? <p className="overlay__desc">{description}</p> : null}
          </div>
          <div className="overlay__headmeta">{meta}</div>
          {closable ? (
            <button
              className="overlay__close"
              onClick={onClose}
              aria-label="关闭"
              title="关闭（Esc）"
            >
              ×
            </button>
          ) : null}
        </header>
        <div className="overlay__body">{children}</div>
        {footer ? <footer className="overlay__foot">{footer}</footer> : null}
      </div>
    </div>
  );
}

export function Modal({
  open,
  onClose,
  title,
  description,
  meta,
  children,
  footer,
  closable = true,
  size = 'md',
}: OverlayBase & { size?: 'md' | 'lg' }) {
  const { panelRef } = useOverlayBehaviour(open, onClose);
  if (!open) return null;

  return (
    <div className="overlay overlay--modal" role="presentation">
      <div className="overlay__scrim" onClick={onClose} aria-hidden="true" />
      <div
        className={`overlay__panel overlay__panel--modal overlay__panel--${size}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={panelRef}
        tabIndex={-1}
      >
        <header className="overlay__head">
          <div className="overlay__headtext">
            <h2 className="overlay__title">{title}</h2>
            {description ? <p className="overlay__desc">{description}</p> : null}
          </div>
          <div className="overlay__headmeta">{meta}</div>
          {closable ? (
            <button
              className="overlay__close"
              onClick={onClose}
              aria-label="关闭"
              title="关闭（Esc）"
            >
              ×
            </button>
          ) : null}
        </header>
        <div className="overlay__body">{children}</div>
        {footer ? <footer className="overlay__foot">{footer}</footer> : null}
      </div>
    </div>
  );
}
